// ─── PRIVACY POLICY ───────────────────────────────────────────────────────────

const LAST_UPDATED = 'April 23, 2026';

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 40 }}>
    <h2 style={{
      fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--accent)',
      marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid var(--border)',
    }}>
      {title}
    </h2>
    <div style={{ fontSize: 15, color: 'var(--text)', lineHeight: 1.75 }}>
      {children}
    </div>
  </section>
);

const P = ({ children, style }) => (
  <p style={{ marginBottom: 12, ...style }}>{children}</p>
);

const UL = ({ items }) => (
  <ul style={{ paddingLeft: 24, marginBottom: 12 }}>
    {items.map((item, i) => <li key={i} style={{ marginBottom: 6 }}>{item}</li>)}
  </ul>
);

const PrivacyPolicy = () => (
  <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '60px 24px 80px' }}>
    <div style={{ maxWidth: 720, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <a
          href="/"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginBottom: 32 }}
        >
          <span style={{ fontSize: 32 }} aria-hidden="true">⏱</span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: 'var(--accent)' }}>Chronos</span>
        </a>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, color: 'var(--text)', marginBottom: 10 }}>
          Privacy Policy
        </h1>
        <p style={{ color: 'var(--text3)', fontSize: 13 }}>Last updated: {LAST_UPDATED}</p>
      </div>

      {/* Intro */}
      <P style={{ color: 'var(--text2)', marginBottom: 48, lineHeight: 1.8 }}>
        Your privacy matters to us. This Privacy Policy explains what information Chronos collects, how we use it,
        and what choices you have. By using Chronos, you agree to the practices described here.
      </P>

      <Section title="1. Information We Collect">
        <P>We collect information you give us directly:</P>
        <UL items={[
          'Account information: your name, email address, and password (stored as a bcrypt hash).',
          'Profile information: avatar, biography, and any other details you choose to add.',
          'Content: timelines, events, comments, and any text or images you post on the platform.',
          'Communications: messages you send to our support team.',
        ]} />
        <P>We collect information automatically when you use the Service:</P>
        <UL items={[
          'Log data: IP address, browser type, pages visited, and timestamps.',
          'Session data: an HttpOnly session cookie used to keep you signed in.',
          'Usage data: features you interact with, timelines you view, and actions you take.',
        ]} />
      </Section>

      <Section title="2. How We Use Your Information">
        <P>We use the information we collect to:</P>
        <UL items={[
          'Create and manage your account.',
          'Provide, maintain, and improve the Service.',
          'Send transactional emails (email verification, password resets).',
          'Respond to your support requests.',
          'Monitor for and prevent abuse, fraud, and violations of our Terms of Service.',
          'Analyse usage trends to understand how the Service is used and improve it.',
        ]} />
        <P>We do not sell your personal information to third parties.</P>
      </Section>

      <Section title="3. How We Share Your Information">
        <P>We share your information only in the following circumstances:</P>
        <UL items={[
          'With your consent: when you make content public or share timelines.',
          'Service providers: trusted vendors who help us operate the Service (e.g. email delivery), bound by confidentiality obligations.',
          'Legal requirements: when required by law, court order, or to protect the rights and safety of our users or the public.',
          'Business transfers: if Chronos is acquired or merged, your data may be transferred as part of that transaction.',
        ]} />
      </Section>

      <Section title="4. Data Retention">
        <P>
          We retain your account information and content for as long as your account is active or as needed to provide
          the Service. If you delete your account, we will delete or anonymise your personal data within 30 days,
          except where we are required to retain it for legal or regulatory reasons.
        </P>
        <P>
          Aggregated or anonymised data (which cannot be used to identify you) may be retained indefinitely for
          analytics purposes.
        </P>
      </Section>

      <Section title="5. Your Rights and Choices">
        <P>Depending on your location, you may have the following rights regarding your personal data:</P>
        <UL items={[
          'Access: request a copy of the personal data we hold about you.',
          'Correction: update or correct inaccurate personal data.',
          'Deletion: request deletion of your personal data ("right to be forgotten").',
          'Portability: receive your data in a structured, machine-readable format.',
          'Objection: object to processing of your personal data in certain circumstances.',
        ]} />
        <P>
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:privacy@chronos.app" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
            privacy@chronos.app
          </a>. We will respond within 30 days.
        </P>
        <P>
          You can update or delete your account information at any time from your profile settings within the app.
        </P>
      </Section>

      <Section title="6. Cookies">
        <P>Chronos uses cookies for the following purposes:</P>
        <UL items={[
          'Session cookie (chronos_session): an HttpOnly, Secure cookie that keeps you signed in. Required for the Service to function.',
          'CSRF token (chronos_csrf): protects against cross-site request forgery attacks.',
        ]} />
        <P>
          We do not use third-party advertising cookies or tracking pixels. The cookies we set are strictly necessary
          for the operation of the Service.
        </P>
      </Section>

      <Section title="7. Security">
        <P>
          We take reasonable technical and organisational measures to protect your information, including:
        </P>
        <UL items={[
          'Passwords are hashed using bcrypt and never stored in plain text.',
          'Session cookies are HttpOnly and Secure.',
          'All data in transit is encrypted using HTTPS/TLS.',
          'CSRF protection on all state-changing requests.',
          'Rate limiting on authentication endpoints.',
        ]} />
        <P>
          No method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect
          your data, we cannot guarantee absolute security.
        </P>
      </Section>

      <Section title="8. Children's Privacy">
        <P>
          Chronos is not directed at children under the age of 13. We do not knowingly collect personal information
          from children under 13. If we become aware that we have collected personal information from a child under 13
          without parental consent, we will take steps to delete it.
        </P>
        <P>
          If you believe we have inadvertently collected information from a child under 13, please contact us
          immediately at{' '}
          <a href="mailto:privacy@chronos.app" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
            privacy@chronos.app
          </a>.
        </P>
      </Section>

      <Section title="9. Changes to This Policy">
        <P>
          We may update this Privacy Policy from time to time. We will notify you of significant changes by email or
          by posting a prominent notice in the app before the change becomes effective. The "Last updated" date at the
          top of this page reflects when the policy was last revised.
        </P>
        <P>
          Your continued use of the Service after the effective date of the revised policy constitutes your acceptance
          of the changes.
        </P>
      </Section>

      <Section title="10. Contact Us">
        <P>
          If you have any questions, concerns, or requests related to this Privacy Policy, please reach out to us at{' '}
          <a href="mailto:privacy@chronos.app" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
            privacy@chronos.app
          </a>.
        </P>
        <P>
          For general inquiries about the Terms of Service, see our{' '}
          <a href="/terms" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Terms of Service</a>.
        </P>
      </Section>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24, textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: 'var(--text3)' }}>
          <a href="/terms" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Terms of Service</a>
          {' · '}
          <a href="/" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Back to Chronos</a>
        </p>
      </div>

    </div>
  </div>
);

export default PrivacyPolicy;
